/**
 * ResolutionModal — Shared component
 *
 * Extracted from ResolutionsPage so it can be reused by
 * MyDocumentsPage (History) and the original Resolutions page.
 *
 * Additions over the original inline modal:
 *  - `initialDraft` prop for pre-populating from History page
 *  - Auto-restore from IndexedDB cache on open
 *  - Fire-and-forget save to IndexedDB + backend after AI generation & on edit close
 */

import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/constants';
import { sanitizeDocHtml } from '../../utils/sanitizeHtml';
import { DOC_CSS_SCOPED } from '../../utils/docCss';
import { DOC_STAGE_STYLE, DOC_PAPER_STYLE, docEditorStyle } from './draftSurfaceStyles';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Icon from '../common/Icon';
import { FiZap, FiShare2, FiRotateCcw } from 'react-icons/fi';
import { TbPencil, TbLock } from 'react-icons/tb';
import DraftStatusChip from '../common/DraftStatusChip';
import ConfirmDialog from '../common/ConfirmDialog';
import HoverPopover from '../common/HoverPopover';
import DraftModeChoicePopover from '../common/DraftModeChoicePopover';
import DocumentSkeleton from '../common/DocumentSkeleton';
import SupportingDocUploader from '../common/SupportingDocUploader';
import DraftOptionsBar from '../common/DraftOptionsBar';
import { getResolutionKind } from '../../data/resolutions';
import { aiAPI, companiesAPI, draftsAPI, documentPdfAPI } from '../../services/api';
import { saveDraft as cacheSave, getDraft as cacheGet, deleteDraft as cacheDelete, NO_CIN_PLACEHOLDER } from '../../services/draftCacheService';
import { useUI } from '../../context/UIContext';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { reportError } from '../../utils/errorReporter';
import ShareDraftModal from './ShareDraftModal';
import { normalizeCompanyContext, stripCodeFences, htmlToPlainText, htmlToMarkdown } from '../../utils/draftUtils';
import { useDocumentExport } from '../../hooks/useDocumentExport';
import { buildDocBasename } from '../../utils/downloadFilename';
import ExportMenu from '../common/ExportMenu';
import { renderDraftHtml } from '../../utils/draftRenderer';
import { getDocumentPreview } from '../../utils/documentPreviews';

/* ── Parse AI plain-text into structured spec for renderDraftHtml ── */
function parseResolutionContent(item, plainText) {
  const lines = stripCodeFences(plainText)
    .replace(/\r\n/g, '\n')
    .split(/\n+/)
    .map((l) => l.trim())
    .filter(Boolean);

  let title = item?.title || 'Board Resolution';
  let section = item?.subtitle || '';
  const bodyParagraphs = [];
  const resolvedClauses = [];
  const furtherResolvedClauses = [];

  lines.forEach((line) => {
    if (/^subject\s*:/i.test(line)) { title = line.replace(/^subject\s*:/i, '').trim() || title; return; }
    if (/^reference\s*:/i.test(line)) { section = line.replace(/^reference\s*:/i, '').trim() || section; return; }
    if (/^(certified|date\s*:|for\s*\[|din\s*:|\[director|\[authorised)/i.test(line)) return;

    if (/^further resolved that/i.test(line)) { furtherResolvedClauses.push(line); return; }
    if (/^resolved that/i.test(line)) { resolvedClauses.push(line); return; }
    bodyParagraphs.push(line);
  });

  return { title, section, bodyParagraphs, resolvedClauses, furtherResolvedClauses };
}

function toResolutionHtml(item, content = '', companyContext = {}) {
  const cleaned = stripCodeFences(content || '');
  if (!cleaned) return buildResolutionFallback(item, companyContext);

  const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(cleaned);
  const plainText = looksLikeHtml ? htmlToPlainText(cleaned) : cleaned;
  if (!plainText.trim()) return buildResolutionFallback(item, companyContext);

  const { title, section, bodyParagraphs, resolvedClauses, furtherResolvedClauses } =
    parseResolutionContent(item, plainText);

  return renderDraftHtml(
    { title, section, isResolution: true, bodyParagraphs, resolvedClauses, furtherResolvedClauses },
    companyContext,
  );
}

function buildResolutionFallback(item, companyContext = {}) {
  return getDocumentPreview(item?.id || '', companyContext);
}

/** Today as the ISO "YYYY-MM-DD" the brand DateField speaks, in LOCAL time. */
function todayIso() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * @param {object}  props
 * @param {object}  props.item            Resolution template object (from resolutions.js)
 * @param {object} props
 * @param {object} props.item            { id, title, icon, subtitle }
 * @param {boolean} props.open
 * @param {Function} props.onClose
 * @param {object} [props.initialDraft]  { docHtml, docSource, draftMeta } — from History page
 * @param {boolean} [props.canEdit=true]  Whether user can edit the document
 */
export default function ResolutionModal({ item, open, onClose, referenceDoc, initialDraft, contentLoading = false, canEdit = true, companyCin, companyName }) {
  const { showToast } = useUI();
  const { activeCompany, companies } = useApp();
  const { user } = useAuth();
  // Share is only offered on a SAVED draft the current user owns (the backend
  // enforces owner-only + no-personal; this just hides the dead affordance).
  const [shareOpen, setShareOpen] = useState(false);
  const canShare = Boolean(
    initialDraft?._id
    && initialDraft?.visibility !== 'personal'
    && String(initialDraft?.user?._id || initialDraft?.user || '') === String(user?.id || user?._id || ''),
  );
  const [docHtml, setDocHtml] = useState('');
  const [loading, setLoading] = useState(false);
  const [companyLoading, setCompanyLoading] = useState(false);
  // Initial-load gate: true from modal open until the company lookup + cache
  // restore resolve. Drives the document-shaped skeleton (not AI generation,
  // which keeps the existing preview visible/editable in place).
  const [initializing, setInitializing] = useState(false);
  const [editable, setEditable] = useState(false);
  const [draftStatus, setDraftStatus] = useState('Preparing editable statutory preview...');
  const [lastDraftMeta, setLastDraftMeta] = useState(null);
  const [showDraftChoice, setShowDraftChoice] = useState(false);
  const [showStatusChip, setShowStatusChip] = useState(false);
  const [docSource, setDocSource] = useState('preview');
  const [supportingDocs, setSupportingDocs] = useState([]);
  // R5 — the date the resolution / meeting bears. Defaults to today (what every
  // template hardcoded before), and feeds BOTH the local preview and the AI
  // prompt so the two can never print different dates.
  const [resolutionDate, setResolutionDate] = useState(todayIso());
  // Optional free-text steer for the next AI draft. Not persisted with the doc.
  const [aiInstructions, setAiInstructions] = useState('');
  // The company record the preview was last built from — kept so a date change
  // can re-render the preview without re-running the whole open effect.
  const [companyCtx, setCompanyCtx] = useState({ name: '', cin: '' });
  // QA-1: deterministic filing-readiness findings for the CURRENT AI draft
  // (cleared on preview/revert — templates are placeholders by design).
  const [lintFindings, setLintFindings] = useState([]);

  const editedRef = useRef(false);
  const contentRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  // Bumps to supersede any in-flight AI draft (doc switch / close / newer call).
  const aiReqRef = useRef(0);
  // Guards the open-effect so it re-inits only on a real (doc, company) change,
  // not when the `companies` array identity churns mid-edit.
  const initSigRef = useRef(null);

  // Sanitize before the dangerouslySetInnerHTML sink — shared policy with every
  // other draft sink (drops scripts / handlers / <img>, keeps styles + tables).
  const safeHtml = useMemo(() => sanitizeDocHtml(docHtml), [docHtml]);

  // Effective CIN: the prop when present, else the active company. Used for
  // both the cache key and the save payload so a save never lands with an
  // empty companyCin (which the backend would mis-derive as "personal").
  const effectiveCin = companyCin || activeCompany?.cin || '';

  // The statutory class this card promises ("u/s 196 · Special Res"). Sent to
  // the backend as a HARD constraint — a special resolution must not come back
  // flattened into a plain board resolution (T7).
  const resolutionKind = useMemo(() => getResolutionKind(item || {}), [item]);

  // Read through a ref so the async AI / cache paths always see the CURRENT
  // date, never the one captured when their promise chain started.
  const dateRef = useRef(resolutionDate);
  dateRef.current = resolutionDate;
  /** Company record + the user's chosen date — what every preview is built from. */
  const withDate = useCallback((co = {}) => ({ ...(co || {}), resolutionDate: dateRef.current }), []);

  const { copy: handleCopy, downloadTxt: handleDownloadTxt, downloadHtml: handleDownloadHtml, print: handlePrint, getHtml: getLatestHtml } = useDocumentExport(contentRef, {
    title: item?.title || 'Resolution Draft',
    filename: buildDocBasename({ company: companyName || activeCompany?.name, docType: item?.title || 'Resolution' }),
    fallbackHtml: docHtml,
    onNotify: showToast,
  });

  // Server-rendered A4 PDF. The browser print dialog stamps the page URL and a
  // timestamp outside the page box where CSS can't reach — not acceptable on a
  // certified true copy. Falls back to that dialog when the renderer is off.
  const [pdfBusy, setPdfBusy] = useState(false);
  const handleDownloadPdf = async () => {
    if (pdfBusy) return;
    setPdfBusy(true);
    try {
      await documentPdfAPI.download({
        title: item?.title || 'Resolution Draft',
        bodyHtml: getLatestHtml(),
        kind: 'resolution',
        cin: effectiveCin,
        filename: buildDocBasename({ company: companyName || activeCompany?.name, docType: item?.title || 'Resolution' }),
      });
      showToast('PDF downloaded', 'success');
    } catch (e) {
      showToast(
        e?.code === 'PDF_DISABLED'
          ? 'Server PDF is unavailable — opening the print dialog instead.'
          : 'PDF export failed — opening the print dialog instead.',
        'warning',
      );
      handlePrint();
    } finally {
      setPdfBusy(false);
    }
  };

  /* ── persist helper (fire-and-forget) ─────────────────────────── */
  const persistDraft = (html, source, meta, activeItem) => {
    const record = {
      documentId: activeItem.id,
      companyCin: effectiveCin,
      companyName: companyName || activeCompany?.name || '',
      documentTitle: activeItem.title,
      documentType: 'resolution',
      pageOrigin: 'resolutions',
      docSource: source,
      docHtml: html,
      draftMeta: meta || {},
    };
    cacheSave(record).catch(() => { });
    draftsAPI.save(record).catch((error) => {
      reportError({
        name: error?.name || 'DraftSaveError',
        message: `ResolutionModal save failed: ${error?.message || String(error)}`,
        stack: error?.stack,
      });
    });
  };

  /** Debounced auto-save (IndexedDB + backend) for refresh recovery & History. */
  const scheduleAutoSave = () => {
    clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      if (!contentRef.current || !item) return;
      persistDraft(contentRef.current.innerHTML, 'user-edited', lastDraftMeta, item);
    }, 2000);
  };

  /* ── AI generation ────────────────────────────────────────────── */
  const runAIDraft = async (activeItem, options = {}) => {
    const reqId = ++aiReqRef.current;
    const useCache = options.useCache !== false;
    const trigger = options.trigger || 'auto';

    setLoading(true);
    setShowStatusChip(true);
    const startedAt = Date.now();
    setDraftStatus(
      useCache
        ? 'AI is processing your draft in the background. You can keep reviewing or editing this preview.'
        : 'Generating a fresh AI draft. This can take up to a minute while preview stays editable.',
    );

    const companyContext = withDate(normalizeCompanyContext(activeCompany || {}));
    try {
      // Collect extracted text from supporting documents (if any)
      const extractedSupportingDocs = supportingDocs
        .filter((d) => d.status === 'extracted' && d.extractedText)
        .map((d) => ({ filename: d.filename, extractedText: d.extractedText, fileType: d.fileType }));

      const response = await aiAPI.draftResolution(
        activeItem.title,
        // Markdown preserves table / list / heading structure across the
        // regeneration round-trip; plain text would flatten tables into a
        // run-together blob and lose all structure.
        htmlToMarkdown(getLatestHtml()) || null,
        companyContext,
        {
          useCache,
          referenceDocument: undefined,
          resolutionId: activeItem.id,
          supportingDocuments: extractedSupportingDocs.length ? extractedSupportingDocs : undefined,
          // R5 — the picked date, ISO. The backend formats it and pins the
          // draft's meetingDate to it instead of "today".
          meetingDate: dateRef.current || undefined,
          // T7 — board / ordinary / special, enforced server-side.
          resolutionKind,
          // Optional user steer for this generation only.
          instructions: aiInstructions.trim() || undefined,
        },
      );
      // Bail if superseded (newer AI call) or the modal moved to a different
      // doc / closed while this ~1-min call was in flight.
      if (reqId !== aiReqRef.current) return;
      const aiHtml = response?.data?.html || response?.html;
      const aiDraft = response?.data?.content || response?.content;
      const draftMeta = response?.data?.telemetry || response?.telemetry || {};
      const draftSource = response?.data?.source || response?.source || '';
      // QA-1 filing-readiness findings computed server-side with the draft.
      setLintFindings(response?.data?.lint?.findings || response?.lint?.findings || []);
      const isTemplateFallback = draftSource === 'fallback' || draftSource === 'ai-fallback-normalized';
      const completedAt = Date.now();

      const hasGenuineAI = !isTemplateFallback && (
        (aiHtml && `${aiHtml}`.trim()) ||
        (aiDraft && `${aiDraft}`.trim())
      );

      if (hasGenuineAI) {
        const meta = {
          ...draftMeta,
          source: draftSource,
          generatedAt: completedAt,
          latencyMs: draftMeta?.latencyMs || completedAt - startedAt,
        };
        setLastDraftMeta(meta);
        const rendered = aiHtml?.trim() || toResolutionHtml(activeItem, `${aiDraft}`, companyContext);
        setDocHtml(rendered);
        setDocSource('ai');
        setDraftStatus(
          draftMeta?.cacheHit
            ? 'Recent draft loaded quickly.'
            : trigger === 'manual'
              ? 'Fresh AI draft generated.'
              : 'AI draft updated successfully.',
        );

        // ── persist draft (fire-and-forget) ──
        persistDraft(rendered, 'ai', meta, activeItem);
      } else {
        setDocHtml(buildResolutionFallback(activeItem, companyContext));
        setDocSource('fallback');
        setLintFindings([]); // templates are placeholders by design \u2014 no nagging
        setDraftStatus('Statutory preview template \u00B7 AI currently unavailable.');
      }
    } catch (err) {
      if (err?.status === 401 || err?.status === 403) return;
      if (reqId !== aiReqRef.current) return;

      setDocHtml(buildResolutionFallback(item, companyContext));
      setDocSource('fallback');
      setLintFindings([]);
      setDraftStatus('Statutory preview template \u00B7 AI currently unavailable.');
    } finally {
      if (reqId === aiReqRef.current) setLoading(false);
    }
  };
  // useCallback so the identity is stable while `companies` is the same —
  // lets the open-effect below list this in its deps array honestly.
  const fetchCompanyByCin = useCallback(async (cin) => {
    // Serve from the already-loaded company list first — avoids a network
    // round-trip on every modal open. A CIN absent from the list falls
    // through to the API (the verify-on-open / 404 safety net stays intact).
    const fromContext = (companies || []).find(
      (c) => String(c.cin || '').toUpperCase() === String(cin || '').toUpperCase()
    );
    if (fromContext) return fromContext;
    try {
      const response = await companiesAPI.getByCin(cin);
      if (response.success) {
        return response.data;
      }
    } catch (error) {
      reportError({
        name: error?.name || 'CompanyLookupError',
        message: `ResolutionModal company lookup failed (cin=${cin}): ${error?.message || String(error)}`,
        stack: error?.stack,
      });
    }
    return { name: '', cin: '' };
  }, [companies]);
  /* ── open effect: auto-restore or initialDraft ────────────────── */
  useEffect(() => {
    // Closed → clear the per-open guard and supersede any in-flight AI draft so a
    // late response can't clobber / persist a dismissed doc.
    if (!open || !item) {
      initSigRef.current = null;
      aiReqRef.current += 1;
      setInitializing(false);
      return;
    }
    // Re-init only on a real (doc, company, source) change — not when `companies`
    // identity churns mid-edit (which would wipe in-progress edits).
    const sig = `${item.id || ''}::${effectiveCin}::${initialDraft ? 'draft' : 'preview'}::${contentLoading ? 'loading' : 'ready'}`;
    if (initSigRef.current === sig) return;
    initSigRef.current = sig;
    aiReqRef.current += 1;
    setLoading(false);
    setInitializing(true);

    // Opened from History before the saved draft has arrived — hold the skeleton
    // (initializing) and skip the cache/preview path until content is passed in.
    if (contentLoading) return;

    const initializeModal = async () => {

      let companyContext = { name: '', cin: '' };

      // Only show the loading overlay when actually fetching — gating on
      // `effectiveCin` keeps the gallery flow interactive (no pointerEvents flash).
      if (effectiveCin) {
        setCompanyLoading(true);
        setDraftStatus('Fetching company details...');
        try {
          companyContext = await fetchCompanyByCin(effectiveCin);
        } catch (error) {
          reportError({
            name: error?.name || 'CompanyLookupError',
            message: `ResolutionModal fetchCompanyByCin threw: ${error?.message || String(error)}`,
            stack: error?.stack,
          });
        }
        setCompanyLoading(false);
      }
      setCompanyCtx(companyContext);

      setEditable(false);
      // Per-open defaults: today's date, no instructions.
      setResolutionDate(todayIso());
      dateRef.current = todayIso();
      setAiInstructions('');
      editedRef.current = false;
      setLastDraftMeta(null);
      setShowDraftChoice(false);
      setShowStatusChip(false);
      setSupportingDocs([]);

      // 1. If opened from History page with initialDraft, use that
      if (initialDraft) {
        setDocHtml(initialDraft.docHtml || '');
        setDocSource(initialDraft.docSource || 'ai');
        setLastDraftMeta(initialDraft.draftMeta || null);
        setDraftStatus('Restored from saved draft.');
        setShowStatusChip(true);
        setInitializing(false);
        return;
      }

      // 2. Try IndexedDB cache for auto-restore
      cacheGet(item.id, effectiveCin).then(async (cached) => {
        if (cached && cached.docHtml) {
          // Verify-on-open \u2014 see DocumentDraftModal. 404 evicts, others keep body.
          if (cached.companyCin && cached.companyCin !== NO_CIN_PLACEHOLDER) {
            try {
              const resp = await companiesAPI.getByCin(cached.companyCin);
              if (!resp || resp.success === false || !resp.data?.cin) {
                throw new Error('company-not-found');
              }
            } catch (err) {
              if (err?.statusCode === 404 || err?.message === 'company-not-found') {
                cacheDelete(cached.documentId, cached.companyCin).catch(() => {});
                setDocSource('preview');
                setDocHtml(buildResolutionFallback(item, withDate(companyContext)));
                setDraftStatus('Previous draft referenced a deleted company. Reset to template.');
                setShowStatusChip(true);
                return;
              }
              // Other errors \u2014 lenient, render cached body.
            }
          }
          setDocHtml(cached.docHtml);
          setDocSource(cached.docSource || 'ai');
          setLastDraftMeta(cached.draftMeta || null);
          setDraftStatus('Restored from your previous draft.');
          setShowStatusChip(true);
        } else {
          setDocSource('preview');
          setDocHtml(buildResolutionFallback(item, withDate(companyContext)));
          setDraftStatus('Preview ready. Click AI Draft to generate an AI-tailored version.');
        }
      }).catch(() => {
        setDocSource('preview');
        setDocHtml(buildResolutionFallback(item, withDate(companyContext)));
        setDraftStatus('Preview ready. Click \u2728 AI Draft to generate an AI-tailored version.');
      }).finally(() => setInitializing(false));
    };

    initializeModal();
    // `withDate` is a stable useCallback (empty deps, reads the date via a ref)
    // — listing it satisfies the linter without making this effect re-run.
  }, [open, item, effectiveCin, initialDraft, contentLoading, fetchCompanyByCin, withDate]);

  /* ── auto-hide status chip ────────────────────────────────────── */
  useEffect(() => {
    if (!open) return;
    if (loading) { setShowStatusChip(true); return; }

    const transientStates = [
      'Recent draft loaded quickly.',
      'Fresh AI draft generated.',
      'Draft prepared using template fallback.',
      'AI draft updated successfully.',
      'Restored from your previous draft.',
      'Restored from saved draft.',
    ];

    if (!transientStates.includes(draftStatus)) return;
    const timer = setTimeout(() => setShowStatusChip(false), 5000);
    return () => clearTimeout(timer);
  }, [open, loading, draftStatus]);

  /* ── date change → re-render the preview ──────────────────────── */
  // Only the untouched TEMPLATE surface follows the picker. An AI / edited /
  // restored document is the user's content — silently re-rendering it from the
  // template on a date change would destroy work. (Those pick the date up on
  // the next AI draft instead, which is an explicit action.)
  const skipDateRenderRef = useRef(true);
  useEffect(() => {
    if (skipDateRenderRef.current) { skipDateRenderRef.current = false; return; }
    if (!open || !item) return;
    if (docSource !== 'preview' || editable || initializing || companyLoading) return;
    setDocHtml(buildResolutionFallback(item, { ...companyCtx, resolutionDate }));
    // Deliberately keyed on the date alone — re-running on docSource/companyCtx
    // churn would clobber an in-progress document.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolutionDate]);

  /* ── Warn on refresh/close while editing + cleanup auto-save ─── */
  useEffect(() => {
    if (!editable) return;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => {
      window.removeEventListener('beforeunload', warn);
      clearTimeout(autoSaveTimerRef.current);
    };
  }, [editable]);

  /* ── Revert to the standard preview ───────────────────────────── */
  // Non-destructive by design: resets the on-screen resolution and clears the
  // DEVICE cache so the next open starts from the template — the AI/edited
  // version already saved to My Documents stays there untouched (persistDraft
  // writes to the backend on every generation/edit). Offered only on template
  // surfaces: a doc opened from My Documents (initialDraft) IS the saved
  // version, so "revert to template" would be meaningless there.
  const [confirmRevert, setConfirmRevert] = useState(false);
  const handleRevert = async () => {
    setConfirmRevert(false);
    if (!item) return;
    aiReqRef.current += 1; // supersede any in-flight AI draft
    clearTimeout(autoSaveTimerRef.current); // a pending autosave must not resurrect it
    let companyContext = { name: '', cin: '' };
    if (effectiveCin) {
      try { companyContext = await fetchCompanyByCin(effectiveCin); } catch { /* preview renders placeholders */ }
    }
    cacheDelete(item.id, effectiveCin).catch(() => {});
    // Fallback templates were never persisted — don't promise a saved copy.
    const hadSavedVersion = docSource === 'ai' || docSource === 'user-edited';
    editedRef.current = false;
    setEditable(false);
    setLintFindings([]);
    setDocHtml(buildResolutionFallback(item, withDate(companyContext)));
    setDocSource('preview');
    setLastDraftMeta(null);
    setDraftStatus('Reverted to the standard preview.');
    setShowStatusChip(true);
    showToast(
      hadSavedVersion
        ? 'Reverted to preview — your previous version is still in My Documents'
        : 'Reverted to the standard preview',
      'success',
    );
  };

  /* ── save on close if user edited ─────────────────────────────── */
  const handleClose = () => {
    const html = getLatestHtml();
    if (editedRef.current) {
      setDocHtml(html);
    }
    if (editedRef.current && item) {
      persistDraft(html, 'user-edited', lastDraftMeta, item);
    }
    editedRef.current = false;
    onClose();
  };

  /* ── toolbar handlers ─────────────────────────────────────────── */
  const handleManualAIDraft = async () => {
    if (!item || loading) return;
    if (lastDraftMeta) { setShowDraftChoice((s) => !s); return; }
    await runAIDraft(item, { useCache: false, trigger: 'manual' });
  };

  const handleChooseRecentDraft = async () => {
    setShowDraftChoice(false);
    await runAIDraft(item, { useCache: true, trigger: 'manual' });
  };

  const handleChooseFreshDraft = async () => {
    setShowDraftChoice(false);
    await runAIDraft(item, { useCache: false, trigger: 'manual' });
  };

  /* Copy/Download/Print handlers provided by useDocumentExport hook */

  /* ── Expand into the AI chat ──────────────────────────────────── */
  // Hands the CURRENT on-screen resolution (including unsaved edits) to the
  // agent as an artifact, in a fresh conversation. Saved drafts keep their
  // identity (_id/refNumber) so save-back can update the original.
  const navigate = useNavigate();
  const handleContinueInChat = () => {
    const html = getLatestHtml();
    if (editedRef.current) setDocHtml(html);
    navigate(ROUTES.AGENT, {
      state: {
        openDraftDoc: {
          _navNonce: `modal-${Date.now()}`,
          _id: initialDraft?._id || null,
          refNumber: initialDraft?.refNumber || null,
          docHtml: html,
          documentTitle: item?.title || initialDraft?.documentTitle || 'Resolution',
          documentType: 'resolution',
          // T7 — the catalog identity travels with the document. It is what tells
          // the chat's draft_resolution tool that this card is (say) "u/s 196 ·
          // Special Res", so a revision in the chat is held to the same statutory
          // class the modal path enforces instead of flattening to a board
          // resolution. Highest-confidence signal available: the user literally
          // opened this card.
          catalogId: item?.id || null,
          docSource,
          companyCin: companyCin || activeCompany?.cin || null,
          companyName: companyName || activeCompany?.name || null,
          createdByName: initialDraft?.createdByName || null,
          createdAt: initialDraft?.createdAt || null,
          updatedAt: initialDraft?.updatedAt || null,
        },
      },
    });
  };

  if (!item) return null;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={
        <div>
          <div className="flex items-center gap-2"><Icon name="📜" size={18} style={{ color: '#145f5f' }} /> {item.title}</div>
          {item.subtitle && <div className="text-[11px] font-normal text-[#7a7060] mt-0.5">{item.subtitle}</div>}
        </div>
      }
      size="xl"
      bodyClassName="p-0"
      containerClassName="!w-[min(1120px,96vw)] max-h-[95vh]"
      headerActions={
        <>
          <HoverPopover variant="tooltip" content={editable ? 'Lock document' : 'Enable edit'}>
            <Button
              size="sm"
              onClick={() => {
                if (editable && contentRef.current) {
                  const html = contentRef.current.innerHTML;
                  setDocHtml(html);
                  if (editedRef.current && item) {
                    setDocSource('user-edited');
                    persistDraft(html, 'user-edited', lastDraftMeta, item);
                  }
                }
                setEditable((s) => !s);
              }}
              disabled={!canEdit}
              iconOnly
              className={`cs-btn--header-action ${editable ? 'active' : ''}`}
              aria-label={editable ? 'Lock document' : 'Enable edit'}
              leftIcon={editable ? <TbLock size={16} /> : <TbPencil size={16} />}
            />
          </HoverPopover>
          <HoverPopover variant="tooltip" content={loading ? 'AI processing…' : 'AI Draft'}>
            <Button
              size="sm"
              iconOnly
              className="cs-btn--header-action"
              aria-label="AI Draft"
              onClick={handleManualAIDraft}
              loading={loading}
              leftIcon={<FiZap size={16} />}
            />
          </HoverPopover>
          {!initialDraft && docSource !== 'preview' && !initializing && (
            <HoverPopover variant="tooltip" content="Revert to standard preview">
              <Button
                size="sm"
                iconOnly
                className="cs-btn--header-action"
                aria-label="Revert to standard preview"
                onClick={() => setConfirmRevert(true)}
                leftIcon={<FiRotateCcw size={15} />}
              />
            </HoverPopover>
          )}
          {/* One Share menu owns sharing AND every export action. The share row
              is always visible for discoverability; when sharing isn't possible
              the hint says exactly why. */}
          <ExportMenu
            size="sm"
            iconOnly
            className="cs-btn--header-action"
            title="Share & export"
            triggerIcon={<FiShare2 size={15} />}
            items={[
              {
                key: 'share',
                label: 'Share with a professional',
                icon: <FiShare2 size={15} />,
                onClick: () => setShareOpen(true),
                disabled: !canShare,
                hint: canShare
                  ? 'Notify a professional and grant read or edit access'
                  : !initialDraft?._id
                    ? 'Open a saved document from My Documents to share it'
                    : initialDraft?.visibility === 'personal'
                      ? 'Personal drafts can’t be shared'
                      : 'Only the document owner can share',
              },
              { key: 'copy',  label: 'Copy text',       onClick: handleCopy, dividerBefore: true },
              { key: 'txt',   label: 'Download .TXT',   onClick: handleDownloadTxt },
              { key: 'html',  label: 'Download .HTML',  onClick: handleDownloadHtml },
              {
                key: 'pdf',
                label: pdfBusy ? 'Preparing PDF…' : 'Download PDF',
                hint: 'Clean A4 — no browser header or page URL',
                onClick: handleDownloadPdf,
                disabled: pdfBusy,
              },
              { key: 'print', label: 'Print / PDF',     hint: 'Browser print dialog', onClick: handlePrint },
            ]}
          />
        </>
      }
    >
      {/* Date the resolution bears + optional AI instructions */}
      <DraftOptionsBar
        date={resolutionDate}
        onDateChange={setResolutionDate}
        dateLabel="Resolution date"
        instructions={aiInstructions}
        onInstructionsChange={setAiInstructions}
        disabled={loading}
      />

      {/* Supporting documents uploader — "process and forget" */}
      <SupportingDocUploader
        documents={supportingDocs}
        onDocumentsChange={setSupportingDocs}
        disabled={loading}
      />

      <div style={DOC_STAGE_STYLE}>
        {/* Sticky overlay — stays visible while scrolling the document */}
        <div className="sticky top-0 z-30" style={{ height: 0, overflow: 'visible' }}>
          <div className="relative">
            <DraftModeChoicePopover
              show={showDraftChoice}
              onCancel={() => setShowDraftChoice(false)}
              onChooseFresh={handleChooseFreshDraft}
              onChooseRecent={handleChooseRecentDraft}
              description="A recent draft exists for this company and resolution. Use it for fastest response, or create a fresh AI draft."
            />

            <DraftStatusChip
              show={showStatusChip}
              loading={loading}
              draftStatus={draftStatus}
              lastDraftMeta={lastDraftMeta}
            />
          </div>
        </div>

        {/* QA-1 filing-readiness strip — deterministic findings for the current AI draft. */}
        {lintFindings.length > 0 && (
          <div className="mx-auto mb-3 px-3.5 py-2.5 rounded-[10px] border flex flex-col gap-1.5" style={{ maxWidth: '820px', background: '#fff7e6', borderColor: '#eadfc0' }}>
            <div className="text-[9.5px] uppercase tracking-[0.5px] font-bold" style={{ color: '#8a6a1f' }}>Before filing</div>
            {lintFindings.slice(0, 4).map((f) => (
              <div key={f.code} className="flex items-start gap-1.5 text-[11px] leading-snug" style={{ color: f.severity === 'warning' ? '#8a6a1f' : '#7a7060' }}>
                <span className="mt-[3px] w-1.5 h-1.5 rounded-full shrink-0" style={{ background: f.severity === 'warning' ? '#b8973a' : '#a09880' }} />
                <span>{f.message}</span>
              </div>
            ))}
          </div>
        )}
        <div
          className="mx-auto bg-white relative"
          style={DOC_PAPER_STYLE}
        >
          {/* Initial-load skeleton — covers the page while the company lookup +
              cache restore resolve (replaces the old centered spinner). The
              DraftStatusChip up top still narrates "Fetching company details…". */}
          {/* Below the z-30 sticky overlays (status chip, AI-chat pill) —
              only needs to cover the z-auto paper content. */}
          {(initializing || companyLoading) && (
            <div className="absolute inset-0 z-10 bg-white overflow-hidden">
              <DocumentSkeleton />
            </div>
          )}

          {/* Prototype CSS for .doc-letterhead / .doc-company-name / .doc-doc-title
              / .doc-resolved / .doc-meeting-table / .doc-sign-block etc. — without
              this the preview HTML renders as unstyled left-aligned text. */}
          <style>{DOC_CSS_SCOPED}</style>
          <div
            ref={contentRef}
            contentEditable={editable && canEdit}
            suppressContentEditableWarning
            onInput={() => { editedRef.current = true; scheduleAutoSave(); }}
            className="doc-preview"
            style={{
              ...docEditorStyle({ editable, canEdit }),
              pointerEvents: (initializing || companyLoading) ? 'none' : 'auto',
            }}
            dangerouslySetInnerHTML={{ __html: safeHtml }}
          />
        </div>

        {/* Floating action — pinned to the MODAL's bottom-left corner while
            scrolling (not the centered paper). Self-labelled, so no tooltip. */}
        <div className="sticky z-30" style={{ bottom: 18, height: 0, overflow: 'visible' }}>
          <div className="pl-1">
            <button
              type="button"
              onClick={handleContinueInChat}
              aria-label="Open this resolution in the AI chat"
              className="inline-flex items-center gap-2 -translate-y-full pl-1.5 pr-3.5 h-9 rounded-full border bg-white text-[12px] font-semibold text-[#145f5f] transition-all duration-200 hover:shadow-xl hover:border-[#145f5f] active:scale-95"
              style={{ borderColor: 'rgba(20,95,95,0.3)', boxShadow: '0 10px 26px -10px rgba(8,17,31,0.35)' }}
            >
              <img src="/vee-mascot.svg" alt="" className="w-6 h-6 rounded-[7px]" />
              Open in AI Chat
            </button>
          </div>
        </div>
      </div>

      <div className="px-4 py-[6px] flex items-center gap-2 border-t" style={{ borderColor: '#ddd6c6', background: '#fff' }}>
        <span
          className="text-[9.5px] px-[7px] py-[2px] rounded-full font-bold shrink-0 inline-flex items-center gap-1"
          style={
            docSource === 'ai'
              ? { background: '#e4f5f3', color: '#145f5f' }
              : docSource === 'fallback'
                ? { background: '#fff7e6', color: '#b8973a' }
                : { background: '#f0ebe0', color: '#7a7060' }
          }
        >
          <Icon name={docSource === 'ai' ? 'ai' : docSource === 'fallback' ? 'warning' : 'review'} size={10} />
          {docSource === 'ai' ? 'AI-generated' : docSource === 'fallback' ? 'Fallback' : 'Preview'}
        </span>
        {initialDraft?.refNumber && (
          <span
            className="text-[9.5px] px-[7px] py-[2px] rounded-full font-semibold shrink-0"
            style={{ background: '#f5f0e8', color: '#7a7060', fontFamily: "'JetBrains Mono', monospace" }}
            title="Document reference number"
          >
            {initialDraft.refNumber}
          </span>
        )}
        <span className="text-[10px] text-[#a09880]">
          Replace all [bracketed placeholders] with company-specific facts before final filing.
        </span>
      </div>

      {canShare && (
        <ShareDraftModal
          open={shareOpen}
          draft={{
            _id: initialDraft._id,
            documentTitle: initialDraft.documentTitle || item?.title,
            companyName: initialDraft.companyName || companyName,
            documentType: initialDraft.documentType,
          }}
          onClose={() => setShareOpen(false)}
        />
      )}

      <ConfirmDialog
        open={confirmRevert}
        onClose={() => setConfirmRevert(false)}
        title="Revert to standard preview?"
        message={<>The resolution on screen resets to the default template preview.</>}
        note="Nothing is deleted — your generated/edited version stays saved and can be re-opened anytime from My Documents."
        confirmLabel="Revert"
        cancelLabel="Keep current"
        onConfirm={handleRevert}
      />
    </Modal>
  );
}
